import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { RefreshCw } from "lucide-react";
import Shell from "@/components/Shell";
import { makeApi } from "@/lib/api";
import RevenueSummary from "@/components/revenue/RevenueSummary";
import RevenueChart from "@/components/revenue/RevenueChart";
import PlanBreakdown from "@/components/revenue/PlanBreakdown";
import CancelledList from "@/components/revenue/CancelledList";
import ClientPlansTable from "@/components/revenue/ClientPlansTable";
import ExportButtons from "@/components/revenue/ExportButtons";

export default function Revenue() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (refresh) => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/revenue", { params: refresh ? { refresh: true } : {} });
      setData(d);
      setError(null);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not load revenue. Try again.");
    } finally {
      setLoading(false);
    }
  }, [api]);
  useEffect(() => { load(false); }, [load]);

  return (
    <Shell>
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono-label text-xs text-[#FFD600] mb-2">OWNER · REVENUE</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl leading-none" data-testid="revenue-title">Revenue.</h1>
          <p className="text-muted-foreground mt-3 text-sm" data-testid="revenue-updated">
            Live from Stripe{data ? ` · updated ${new Date(data.generated_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => load(true)} disabled={loading} data-testid="revenue-refresh"
            className="min-h-[44px] px-4 cta-primary font-mono-label text-[11px] inline-flex items-center gap-2 disabled:opacity-60">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <ExportButtons api={api} />
        </div>
      </div>
      {error && <div className="brutal-border border-[#F87171] text-sm text-[#F87171] p-3 mb-6" data-testid="revenue-error">{error}</div>}
      {!data && !error && <div className="font-mono-label text-xs text-muted-foreground">LOADING FROM STRIPE…</div>}
      {data && (
        <>
          <RevenueSummary data={data} />
          <RevenueChart monthly={data.monthly} />
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <PlanBreakdown plans={data.plans} />
            <CancelledList items={data.cancelled} />
          </div>
          <ClientPlansTable clients={data.clients} />
        </>
      )}
    </Shell>
  );
}
